<section class="px-6 py-20 sm:px-10">
  <div class="mx-auto max-w-5xl">
    <p class="mb-6">
      <a
        routerLink="/admin"
        class="text-sm text-dusk hover:text-parchment hover:underline"
        >&larr; Back to admin dashboard</a
      >
    </p>

    <div class="mb-8 flex flex-wrap items-start justify-between gap-4">
      <div>
        <span class="mb-4 block text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-gold"
          >Site admin</span
        >
        <h1 class="font-display text-3xl font-semibold text-parchment">People catalog</h1>
      </div>
      <div class="flex flex-wrap items-start gap-3">
        @if (!showAddSearch()) {
          <button
            type="button"
            (click)="openAddSearch()"
            class="inline-flex items-center justify-center rounded-[3px] border border-hairline px-4 py-2 text-sm font-semibold text-parchment transition hover:border-gold hover:text-gold"
          >
            + Add person
          </button>
        }
        <button
          type="button"
          (click)="openBulkAdd()"
          class="inline-flex items-center justify-center rounded-[3px] border border-hairline px-4 py-2 text-sm font-semibold text-parchment transition hover:border-gold hover:text-gold"
        >
          + Bulk add people
        </button>
      </div>
    </div>

    @if (showAddSearch()) {
      <div class="mb-8 rounded-md border border-hairline bg-panel p-4">
        <div class="mb-3 flex items-center justify-between gap-4">
          <h2 class="text-sm font-semibold uppercase tracking-wide text-dusk">Add a person</h2>
          <button
            type="button"
            (click)="closeAddSearch()"
            class="text-xs text-dusk hover:text-parchment hover:underline"
          >
            Cancel
          </button>
        </div>

        <form [formGroup]="addSearchForm" (ngSubmit)="searchToAdd()" class="flex gap-3">
          <input
            formControlName="q"
            type="text"
            placeholder="Search for a celebrity…"
            class="flex-1 rounded border border-hairline bg-ink px-4 py-2.5 text-parchment placeholder:text-dusk/60 focus:border-gold focus:outline-none"
          />
          <button
            type="submit"
            [disabled]="addSearching()"
            class="inline-flex shrink-0 items-center justify-center rounded-[3px] bg-gradient-to-b from-gold to-gold-dim px-5 py-2.5 text-sm font-bold text-ink transition hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-60"
          >
            {{ addSearching() ? 'Searching…' : 'Search' }}
          </button>
        </form>

        @if (addSearchResults().length > 0) {
          <ng-template #addResultRow let-result>
            <button
              type="button"
              (click)="selectAddResult(result)"
              class="flex w-full items-center gap-3 rounded p-3 text-left transition hover:bg-parchment/[0.04]"
            >
              @if (result.imageUrl) {
                <img [src]="result.imageUrl" alt="" class="h-12 w-12 shrink-0 rounded-full object-cover" />
              }
              <div>
                <p class="font-medium text-parchment">
                  {{ result.name }}{{ result.aliases?.length ? ' "' + result.aliases.join('", "') + '"' : '' }}{{ result.knownFor ? ' (' + result.knownFor + ')' : '' }}
                </p>
                <p class="mt-1 text-xs uppercase tracking-wide text-dusk">
                  {{ result.source }}
                  @if (result.status === 'DEAD') {
                    · Deceased
                  }
                  @if (result.source === 'wikipedia' && result.dateOfBirth === null) {
                    · no confirmed birth date
                  }
                </p>
              </div>
            </button>
          </ng-template>

          <div class="mt-3 flex flex-col gap-1 border-t border-hairline pt-3">
            @for (result of addVisibleResults(); track addResultKey(result)) {
              <ng-container [ngTemplateOutlet]="addResultRow" [ngTemplateOutletContext]="{ $implicit: result }" />
            }

            @if (addHiddenResults().length > 0) {
              <button
                type="button"
                (click)="addShowHidden.set(!addShowHidden())"
                class="rounded px-3 py-1.5 text-left text-xs text-dusk transition hover:bg-parchment/[0.04] hover:text-parchment hover:underline"
              >
                {{ addShowHidden() ? 'Hide' : 'Show ' + addHiddenResults().length + ' Hidden' }}
              </button>
              @if (addShowHidden()) {
                @for (result of addHiddenResults(); track addResultKey(result)) {
                  <ng-container [ngTemplateOutlet]="addResultRow" [ngTemplateOutletContext]="{ $implicit: result }" />
                }
              }
            }

            @if (addVisibleResults().length > 0 && !addWikipediaAlsoSearched()) {
              <button
                type="button"
                (click)="searchToAdd(true)"
                [disabled]="addSearching()"
                class="rounded px-3 py-1.5 text-left text-xs text-dusk transition hover:bg-parchment/[0.04] hover:text-parchment hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              >
                Not the right person? Search Wikipedia
              </button>
            }
          </div>
        }

        @if (addNoResultsFound()) {
          <p class="mt-3 text-sm text-dusk">No results found — nothing in the catalog or on Wikipedia matched that search.</p>
        }

        @if (addSearchError()) {
          <p class="mt-3 rounded border border-wax/40 bg-wax/10 px-4 py-3 text-sm text-parchment">
            {{ addSearchError() }}
          </p>
        }
      </div>
    }

    <form [formGroup]="searchForm" (ngSubmit)="search()" class="mb-6 flex gap-3">
      <input
        formControlName="q"
        type="text"
        placeholder="Filter by name…"
        class="flex-1 rounded border border-hairline bg-ink px-4 py-2.5 text-parchment placeholder:text-dusk/60 focus:border-gold focus:outline-none"
      />
      <button
        type="submit"
        class="inline-flex shrink-0 items-center justify-center rounded-[3px] bg-gradient-to-b from-gold to-gold-dim px-5 py-2.5 text-sm font-bold text-ink transition hover:-translate-y-px"
      >
        Search
      </button>
    </form>

    @if (loading()) {
      <p class="text-dusk">Loading…</p>
    } @else if (errorMessage()) {
      <p class="rounded border border-wax/40 bg-wax/10 px-4 py-3 text-sm text-parchment">
        {{ errorMessage() }}
      </p>
    } @else if (items().length === 0) {
      <p class="text-sm text-dusk">No people match.</p>
    } @else {
      <div class="rounded-md border border-hairline bg-panel">
        <ul class="flex flex-col divide-y divide-hairline">
          @for (person of items(); track person.id) {
            <li>
              <button
                type="button"
                (click)="open(person)"
                class="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm transition hover:bg-parchment/[0.04]"
              >
                <span class="flex items-center gap-2 text-parchment">
                  @if (person.imageUrl) {
                    <img
                      [src]="person.imageUrl"
                      alt=""
                      class="h-7 w-7 shrink-0 rounded-full object-cover"
                    />
                  }
                  {{ person.name }}
                  @if (person.knownFor) {
                    <span class="text-xs text-dusk">({{ person.knownFor }})</span>
                  }
                </span>
                <span class="shrink-0 text-xs uppercase tracking-wide text-dusk">
                  @if (person.status === 'DEAD') {
                    <span class="text-wax-bright">Deceased</span>
                  } @else {
                    Alive
                  }
                </span>
              </button>
            </li>
          }
        </ul>
      </div>

      <div class="mt-4 flex items-center justify-between text-sm">
        <button
          type="button"
          (click)="goToPage(page() - 1)"
          [disabled]="page() <= 1"
          class="text-dusk hover:text-parchment disabled:cursor-not-allowed disabled:opacity-40"
        >
          &larr; Previous
        </button>
        <span class="text-xs uppercase tracking-wide text-dusk">
          Page {{ page() }} of {{ totalPages() }} ({{ total() }} total)
        </span>
        <button
          type="button"
          (click)="goToPage(page() + 1)"
          [disabled]="page() >= totalPages()"
          class="text-dusk hover:text-parchment disabled:cursor-not-allowed disabled:opacity-40"
        >
          Next &rarr;
        </button>
      </div>
    }

    @if (modalOpen()) {
      <app-person-modal
        [personId]="openPersonId()"
        [prefill]="addPrefill()"
        (closed)="onModalClosed()"
        (changed)="onModalChanged()"
      />
    }

    @if (bulkAddOpen()) {
      <app-bulk-add-people-modal (closed)="closeBulkAdd()" (changed)="onModalChanged()" />
    }
  </div>
</section>
